/**
 * Infrastructure reference — owner-only single-page summary of all platforms,
 * URLs, credentials locations for the NexKoins stack.
 *
 * Purpose: one place to find "where does X live" without hunting through docs.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Globe, Database, Server, Lock, Github, Cloud, CreditCard, Mail, Zap, HardDrive, Shield } from 'lucide-react';

const SECTIONS = [
  {
    title: 'Domains & Subdomains',
    icon: Globe,
    color: '#7a8b8f',
    items: [
      { name: 'nexkoins.com', purpose: 'Root domain', where: 'GoDaddy', url: 'https://dcc.godaddy.com/manage/NEXKOINS.COM/dns', status: 'Managed' },
      { name: 'hub.nexkoins.com', purpose: 'UniHub (this app)', where: 'Netlify → nk-hub', url: 'https://hub.nexkoins.com', status: 'Live' },
      { name: 'ledger.nexkoins.com', purpose: 'NK Ledger (Flask app)', where: 'VPS → Traefik', url: 'https://ledger.nexkoins.com', status: 'Live' },
      { name: 'office.nexkoins.com', purpose: 'NK Office (renamed from invest.)', where: 'Netlify → nk-invest (alias to office.)', url: 'https://office.nexkoins.com', status: 'Live' },
      { name: 'invest.nexkoins.com', purpose: '301 redirect → office.nexkoins.com', where: 'Netlify redirect', url: 'https://invest.nexkoins.com', status: 'Redirect' },
      { name: 'ea.nexkoins.com', purpose: 'NK EA Bot (Telegram strategic brain)', where: 'VPS → Traefik (Docker port 5002)', url: 'https://ea.nexkoins.com', status: 'Live' },
      { name: 'ai.nexkoins.com', purpose: 'AI Gateway (LiteLLM proxy for all NK AI calls)', where: 'VPS → Traefik (Docker port 4000)', url: 'https://ai.nexkoins.com', status: 'Live' },
      { name: 'folio.nexkoins.com', purpose: 'Folio (insurance vault SaaS)', where: 'VPS → Traefik', url: 'https://folio.nexkoins.com', status: 'Live' },
      { name: 'n8n.nexkoins.com', purpose: 'N8N workflow automation (DNS only, no public route)', where: 'VPS internal :32768', url: null, status: 'DNS ready' },
      { name: 'teamkng.com', purpose: 'KNG public website (Wix → migrating to Next.js)', where: 'Wix DNS → migrating to Netlify (kng-test)', url: 'https://teamkng.com', status: 'DNS migration pending Apr 30' },
    ],
  },
  {
    title: 'Hosting',
    icon: Cloud,
    color: '#b6934a',
    items: [
      { name: 'Hostinger VPS (srv1449523)', purpose: 'Ledger + EA Bot + AI Gateway + Folio + N8N + Postgres + Traefik', where: 'Ubuntu 24.04 · 15GB RAM · 4 CPU · 193GB disk · 187.77.129.241', url: 'https://hpanel.hostinger.com', status: 'Active' },
      { name: 'Netlify — nk-hub', purpose: 'UniHub (React)', where: 'Manual drag-drop deploy (no auto-build)', url: 'https://app.netlify.com', status: 'Active' },
      { name: 'Netlify — nk-invest', purpose: 'NK Office (React) — site name still nk-invest', where: 'Manual drag-drop deploy', url: 'https://app.netlify.com', status: 'Active' },
      { name: 'Netlify — kng-test', purpose: 'KNG public website (Next.js)', where: 'Manual drag-drop deploy · custom domain pending teamkng.com', url: 'https://kng-test.netlify.app', status: 'Active' },
      { name: 'Netlify — khaonoigym-internal-ostesting', purpose: 'KNG-OS staff portal (blog admin)', where: 'Manual drag-drop deploy', url: 'https://khaonoigym-internal-ostesting.netlify.app', status: 'Active' },
    ],
  },
  {
    title: 'Databases',
    icon: Database,
    color: '#a83238',
    items: [
      { name: 'Postgres 16 — `ledger` DB', purpose: 'NK Ledger multi-entity financial data + EA memory (ea schema with pgvector)', where: 'VPS localhost:5432 · firewall blocks external', url: null, status: 'Primary' },
      { name: 'Postgres 16 — `litellm` DB (isolated 2026-04-27)', purpose: 'LiteLLM proxy state — separated to prevent app-level wipes affecting ledger', where: 'VPS localhost:5432 · cross-DB reads via postgres_fdw to ledger.ai_gateway', url: null, status: 'Active' },
      { name: 'Firestore (nexkoins-portfoliohub)', purpose: 'UniHub — businesses, roles, documents, claims, contacts, alerts', where: 'Firebase console', url: 'https://console.firebase.google.com/project/nexkoins-portfoliohub/firestore', status: 'Primary' },
      { name: 'Firestore (kng-content-manager)', purpose: 'KNG-OS content (blog posts, etc)', where: 'Separate Firebase project', url: 'https://console.firebase.google.com/project/kng-content-manager/firestore', status: 'Active' },
      { name: 'Supabase (vlzlrxadaeiwurotraxu)', purpose: 'NK Office (entities, holdings, dividends, accounts) + Folio + tasks + receipts storage bucket', where: 'Supabase dashboard', url: 'https://supabase.com/dashboard/project/vlzlrxadaeiwurotraxu', status: 'Primary' },
      { name: 'ledger.db (legacy SQLite)', purpose: 'Pre-Apr-2026 KNG ledger snapshot — used as restoration source after Apr 27 wipe', where: 'Local: c:/EA/projects/kng/bookkeeping/ledger/', url: null, status: 'Archive (gold copy)' },
    ],
  },
  {
    title: 'Authentication',
    icon: Lock,
    color: '#1f7a4d',
    items: [
      { name: 'Firebase Auth (nexkoins-portfoliohub)', purpose: 'UniHub + Ledger + Folio shared auth (federated from UniHub)', where: 'Firebase console → Authentication', url: 'https://console.firebase.google.com/project/nexkoins-portfoliohub/authentication/users', status: 'Active' },
      { name: 'Owner email', purpose: 'Auto-grants admin on all ledger entities + UniHub roles', where: 'ENV OWNER_EMAIL on VPS', url: null, status: 'nigeltan88@outlook.com' },
      { name: 'AI Gateway virtual keys', purpose: '8 vk-* keys minted from master Anthropic + OpenAI keys (per-project quotas)', where: 'See c:/EA/secrets/nk-master.env "AI Gateway virtual keys"', url: 'https://ai.nexkoins.com', status: 'Active' },
      { name: 'Telegram bot whitelist', purpose: 'Only Kai\'s user ID (8265352104) can interact with EA Bot', where: 'EA Bot ENV KAI_TELEGRAM_USER_ID', url: null, status: 'Single-user' },
    ],
  },
  {
    title: 'Code & Deploy',
    icon: Github,
    color: '#3a3a3a',
    items: [
      { name: 'GitHub — Kai88-io/nexkoins-hub', purpose: 'All code (private monorepo)', where: 'GitHub web', url: 'https://github.com/Kai88-io/nexkoins-hub', status: 'Primary repo' },
      { name: 'Local working copy', purpose: 'Dev machine — moved out of OneDrive', where: 'c:\\EA\\', url: null, status: 'Active' },
      { name: 'VPS clone', purpose: '/opt/nexkoins-hub/projects/ — ledger, ea-bot, ai-gateway, folio', where: 'scp + docker compose up -d --build', url: null, status: 'Active' },
    ],
  },
  {
    title: 'Reverse Proxy & SSL',
    icon: Shield,
    color: '#a57a9c',
    items: [
      { name: 'Traefik (Docker on VPS)', purpose: 'HTTPS routing for ledger / ea / ai / folio subdomains', where: 'Docker container: traefik-traefik-1', url: null, status: 'Active' },
      { name: "Let's Encrypt", purpose: 'SSL certs auto-renewed by Traefik for all *.nexkoins.com routes', where: '/var/lib/docker/volumes/traefik_traefik-letsencrypt', url: null, status: 'Auto' },
      { name: 'Netlify SSL', purpose: 'hub + invest/office + kng-test + khaonoigym-internal Netlify sites', where: 'Managed by Netlify, Let\'s Encrypt', url: null, status: 'Auto' },
    ],
  },
  {
    title: 'Automation & Ops',
    icon: Zap,
    color: '#d4a840',
    items: [
      { name: 'AI Gateway (LiteLLM in Docker)', purpose: 'Single proxy for all Claude + OpenAI calls. Tracks per-key spend, enforces budgets.', where: 'VPS Docker · ai-gateway container · port 4000', url: 'https://ai.nexkoins.com', status: 'Live (since 2026-04-27)' },
      { name: 'NK EA Bot (Telegram)', purpose: '@NexKoinsEABot — strategic EA, memory, tools, voice notes, proactive digests', where: 'VPS Docker · ea-bot container · port 5002', url: 'https://ea.nexkoins.com', status: 'Live (Phase 1 complete)' },
      { name: '@NexKoinsAssistantBot (Telegram)', purpose: 'Operations bot — receipt OCR, /ask Q&A, /tasks commands', where: 'N8N workflows on VPS', url: null, status: 'Active' },
      { name: 'N8N (Docker on VPS)', purpose: 'Workflow automation: receipt OCR, anomaly alerts, /ask, tasks bot', where: 'VPS internal :32768', url: null, status: 'Active' },
      { name: 'nk-memory-api (systemd)', purpose: 'NK Office investment memory API', where: 'VPS :8000 (internal)', url: null, status: 'Active' },
      { name: 'Local pg_dumpall backup', purpose: 'Nightly Postgres dump (ledger + litellm) with 14-day retention', where: '/var/backups/postgresql/ · cron 02:00 SGT', url: null, status: 'Cron 02:00 SGT' },
      { name: 'Legacy ledger backup', purpose: 'pg_dump of ledger DB only — daily/weekly/monthly tiers', where: '/var/backups/ledger/', url: null, status: 'Cron 03:00 SGT' },
      { name: 'R2 off-VPS backup pipeline', purpose: '8 sources → Cloudflare R2 (postgres-local, supabase-nkoffice, supabase-folio, firestore-unihub, etc). Failure alerts via @NexKoinsEABot.', where: 'VPS · cron 02:00 SGT', url: 'https://dash.cloudflare.com', status: 'Live (since 2026-04-28)' },
      { name: 'Investment Agent (Telegram)', purpose: 'Daily market digest', where: 'VPS · scheduled', url: null, status: 'Active' },
    ],
  },
  {
    title: 'External Services',
    icon: CreditCard,
    color: '#627175',
    items: [
      { name: 'Stripe (KNG)', purpose: 'Membership + lesson payment processing', where: 'Stripe dashboard', url: 'https://dashboard.stripe.com', status: 'Active' },
      { name: 'UOB (Bank 1)', purpose: 'KNG primary operating bank', where: 'UOB Business internet banking', url: 'https://www.uob.com.sg/bib', status: 'Active' },
      { name: 'Cloudflare R2', purpose: 'Off-VPS backup storage (nk-backups bucket, $0/mo within free tier)', where: 'Cloudflare dashboard → R2', url: 'https://dash.cloudflare.com', status: 'Active' },
      { name: 'Anthropic API', purpose: 'Claude (via AI Gateway only — no direct project keys)', where: 'Master key in AI Gateway', url: 'https://console.anthropic.com', status: 'Routed via Gateway' },
      { name: 'OpenAI API', purpose: 'Whisper (voice → text), text embeddings (RAG memory), via AI Gateway', where: 'Master key in AI Gateway', url: 'https://platform.openai.com', status: 'Routed via Gateway' },
      { name: 'Telegram bots', purpose: '@NexKoinsEABot (EA), @NexKoinsAssistantBot (ops), @IvanQuantTrader (investing), Jane bot (memory)', where: 'BotFather', url: null, status: 'Active' },
    ],
  },
  {
    title: 'Secrets Storage',
    icon: HardDrive,
    color: '#8a6b4e',
    items: [
      { name: 'NK Master Env File', purpose: 'Single source of truth for ALL credentials (rotate, look up, audit)', where: 'c:/EA/secrets/nk-master.env (gitignored)', url: null, status: 'Locked (local only)' },
      { name: '.env on VPS — Ledger', purpose: 'DB password, Firebase admin', where: '/opt/nexkoins-hub/projects/ledger/.env (chmod 600)', url: null, status: 'Locked' },
      { name: '.env on VPS — EA Bot', purpose: 'Telegram token, Anthropic + OpenAI keys, Supabase, DB', where: '/opt/nexkoins-hub/projects/ea-bot/.env (chmod 600)', url: null, status: 'Locked' },
      { name: '.env on VPS — AI Gateway', purpose: 'Master Anthropic + OpenAI keys, Postgres URL → litellm DB', where: '/opt/nexkoins-hub/projects/ai-gateway/.env (chmod 600)', url: null, status: 'Locked' },
      { name: 'Firebase admin JSON', purpose: 'Ledger Firebase token verification', where: '/opt/ledger/firebase-creds.json (chmod 600)', url: null, status: 'Locked' },
      { name: 'GitHub PAT', purpose: 'VPS pulling private repo', where: 'Embedded in /opt/nexkoins-hub/.git/config', url: null, status: 'Restricted: repo scope only' },
      { name: 'Local .env files', purpose: 'Dev environment vars', where: 'c:/EA/.env.* (gitignored)', url: null, status: 'Not in repo' },
    ],
  },
];

const ARCHITECTURE_NODES = [
  { id: 'godaddy', label: 'GoDaddy DNS', x: 400, y: 40, type: 'dns' },
  { id: 'cf-lb', label: 'Netlify CDN', x: 140, y: 160, type: 'cdn' },
  { id: 'traefik', label: 'Traefik (HTTPS)', x: 660, y: 160, type: 'proxy' },
  { id: 'hub', label: 'UniHub', x: 60, y: 290, type: 'app' },
  { id: 'invest', label: 'NK Invest', x: 220, y: 290, type: 'app' },
  { id: 'ledger', label: 'Ledger (Flask)', x: 560, y: 290, type: 'app' },
  { id: 'n8n', label: 'n8n', x: 720, y: 290, type: 'app' },
  { id: 'memapi', label: 'nk-memory-api', x: 880, y: 290, type: 'app' },
  { id: 'pg', label: 'Postgres 16', x: 560, y: 440, type: 'db' },
  { id: 'fs', label: 'Firestore', x: 60, y: 440, type: 'db' },
  { id: 'sb', label: 'Supabase', x: 220, y: 440, type: 'db' },
  { id: 'fbauth', label: 'Firebase Auth', x: 310, y: 560, type: 'auth' },
];

const ARCHITECTURE_EDGES = [
  ['godaddy', 'cf-lb'], ['godaddy', 'traefik'],
  ['cf-lb', 'hub'], ['cf-lb', 'invest'],
  ['traefik', 'ledger'], ['traefik', 'n8n'], ['traefik', 'memapi'],
  ['ledger', 'pg'], ['hub', 'fs'], ['invest', 'sb'], ['hub', 'fbauth'], ['ledger', 'fbauth'], ['hub', 'ledger'],
];

function nodeColor(type) {
  return {
    dns: '#b6934a', cdn: '#7a8b8f', proxy: '#a57a9c',
    app: '#3a3a3a', db: '#a83238', auth: '#1f7a4d',
  }[type] || '#3a3a3a';
}

export default function Infrastructure({ user }) {
  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <Link to="/" className="btn"><ArrowLeft size={16} /> Back</Link>
        <h1 style={{ margin: 0, fontSize: 24 }}>Infrastructure Reference</h1>
        <span style={{ fontSize: 11, padding: '3px 10px', background: '#fee2a3', color: '#7a5c00', borderRadius: 10, textTransform: 'uppercase', fontWeight: 600 }}>Owner Only</span>
      </div>
      <p style={{ color: '#6b6b6b', marginTop: 0, marginBottom: 24, fontSize: 14 }}>
        Single source of truth for all platforms, URLs, credentials locations in the NexKoins stack.
        Not linked publicly. Only visible to the owner.
      </p>

      {/* Architecture diagram */}
      <div style={{ background: '#fff', border: '1px solid #e4e4e2', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 16, textTransform: 'uppercase', color: '#6b6b6b', letterSpacing: 0.5 }}>Architecture</h2>
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <svg viewBox="0 0 980 620" style={{ width: '100%', minWidth: 700, maxWidth: 1100, height: 'auto', display: 'block' }}>
            {/* Edges */}
            {ARCHITECTURE_EDGES.map(([a, b], i) => {
              const na = ARCHITECTURE_NODES.find((n) => n.id === a);
              const nb = ARCHITECTURE_NODES.find((n) => n.id === b);
              return <line key={i} x1={na.x + 80} y1={na.y + 30} x2={nb.x + 80} y2={nb.y + 30} stroke="#b9b9b5" strokeWidth="1.5" strokeDasharray="4,3" />;
            })}
            {/* Nodes */}
            {ARCHITECTURE_NODES.map((n) => (
              <g key={n.id} transform={`translate(${n.x},${n.y})`}>
                <rect x="0" y="0" width="160" height="60" rx="8" fill={nodeColor(n.type)} opacity="0.95" />
                <text x="80" y="35" fill="#fff" fontSize="13" fontWeight="600" textAnchor="middle">{n.label}</text>
              </g>
            ))}
            {/* Legend */}
            <g transform="translate(20, 590)">
              {[['dns', 'DNS'], ['cdn', 'CDN'], ['proxy', 'Proxy'], ['app', 'App'], ['db', 'DB'], ['auth', 'Auth']].map(([t, label], i) => (
                <g key={t} transform={`translate(${i * 90}, 0)`}>
                  <rect width="12" height="12" rx="2" fill={nodeColor(t)} />
                  <text x="18" y="10" fontSize="11" fill="#6b6b6b">{label}</text>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </div>

      {/* Section grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 }}>
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.title} style={{ background: '#fff', border: '1px solid #e4e4e2', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: s.color, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon size={18} /><strong style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.title}</strong>
              </div>
              <div>
                {s.items.map((it, idx) => (
                  <div key={idx} style={{ padding: '12px 16px', borderBottom: idx < s.items.length - 1 ? '1px solid #f0f0ed' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                          {it.url ? (
                            <a href={it.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a', textDecoration: 'none' }}>
                              {it.name} <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
                            </a>
                          ) : it.name}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b6b6b', marginBottom: 2 }}>{it.purpose}</div>
                        <div style={{ fontSize: 11, color: '#9b9b97', fontFamily: 'monospace' }}>{it.where}</div>
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 8px', background: '#f0f0ed', color: '#6b6b6b', borderRadius: 10, whiteSpace: 'nowrap', marginTop: 2 }}>{it.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer — quick-access links */}
      <div style={{ marginTop: 30, padding: 16, background: '#fafaf8', border: '1px solid #e4e4e2', borderRadius: 10 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 13, textTransform: 'uppercase', color: '#6b6b6b' }}>Quick Access</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            ['Netlify', 'https://app.netlify.com'],
            ['Firebase', 'https://console.firebase.google.com/project/nexkoins-portfoliohub'],
            ['Supabase', 'https://supabase.com/dashboard'],
            ['Hostinger VPS', 'https://hpanel.hostinger.com'],
            ['GoDaddy DNS', 'https://dcc.godaddy.com/manage/NEXKOINS.COM/dns'],
            ['GitHub Repo', 'https://github.com/Kai88-io/nexkoins-hub'],
            ['Stripe', 'https://dashboard.stripe.com'],
          ].map(([label, url]) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ textDecoration: 'none' }}>
              <ExternalLink size={12} /> {label}
            </a>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 11, color: '#9b9b97', textAlign: 'center' }}>
        Viewing as <strong>{user?.email}</strong> · Owner-only view · Last updated from source: {(() => {
          const d = new Date();
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
        })()}
      </div>
    </div>
  );
}
